/* ============================================================
   dbakers-cookie-builder.js
   ASSET file. Lives in assets/.

   IIFE-wrapped (no globals leak). Reads window.dcooConfig from the
   Liquid snippet for product/variant/addon data.

   ── 2026-06-12 — live inventory backstop ──
   The Liquid now renders sold-out flavor cards with `disabled` +
   data-sold-out="true" (driven by product.available per handle).
   A disabled <button> never fires click events, but bindFlavorCards
   carries an explicit guard anyway so CSS/markup drift can never
   let a sold-out cookie into the box.

   ── 2026-06-12 (b) — per-flavor quantity caps ──
   Each card also carries data-flavor-qty (live Shopify quantity,
   9999 = untracked/unlimited). bindFlavorCards enforces it as a hard
   cap: when the box already holds qty-of-flavor === stock, the tap
   no-ops, the card shakes + flashes "Only N available" (flashMaxed),
   and renderFlavorCounts keeps an .is-maxed class on the card until
   the customer removes one. Removing from the box automatically
   un-maxes the card (renderFlavorCounts runs on every mutation).

   ── 2026-06-12 (c) — inventory decrement payload ──
   buildBoxLineProperties now writes a hidden _cookie_inv property on
   the box line: "handle:count|handle:count" (sorted, handles from
   data-flavor-handle). CookieInventory.gs in the autolist Apps Script
   project parses it on the order webhook and decrements each flavor's
   Shopify inventory via inventoryAdjustQuantities — closing the loop
   so the render-time sold-out/quantity caps reflect real sales.

   State machine:
     - state.boxSize  6 or 12 (matches the selected variant)
     - state.cookies  Array of {name, key, tier} — one entry per cookie
                      slot in the box, in tap order
     - state.giftMsg  string (optional)
     - state.addons   Set of { id, label, price } for chosen candles/sparklers

   Slot accounting:
     - state.cookies.length tells us "filled" count
     - boxSize - state.cookies.length = remaining slots
     - When state.cookies.length === boxSize → step 2 complete

   Cart submit (two-stage):
     1. Add the box variant (1 unit) — get back the line key
     2. Batch all premium upcharges + make-it-special addons in one
        /cart/add.js call with _addon_for: <key> linking each line to
        the parent box. The cart template renders the addons indented
        under the box, total rolls up via cart.original_total_price.

   ============================================================ */
(function() {
  'use strict';

  var cfg = window.dcooConfig;
  if (!cfg) {
    console.warn('[dcoo] window.dcooConfig missing — builder will not initialise.');
    return;
  }

  /* ============================================================
     UTILITIES
     ============================================================ */
  function $(id) { return document.getElementById(id); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, evt, fn) { if (el) el.addEventListener(evt, fn); }
  function setText(id, text) { var el = $(id); if (el) el.textContent = text; }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function(c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }
  function money(cents) {
    /* Shopify gives prices in cents. Cookie box prices are whole dollars
       but we format defensively so anything that comes through looks
       correct. Mirrors Shopify's default money filter ($X.XX). */
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  /* ============================================================
     STATE
     ============================================================ */
  var state = {
    boxSize:        cfg.boxSize,
    currentVariant: cfg.currentVariantId,
    currentPrice:   cfg.currentVariantPrice,
    cookies:        [],  /* [{name, key, tier}] */
    giftMsg:        '',
    addons:         []   /* [{id, label, price}] */
  };

  /* ============================================================
     STEP MACHINERY — accordion open/close
     ============================================================ */
  function unlockStep(stepEl) {
    if (!stepEl) return;
    stepEl.classList.remove('is-locked');
    stepEl.classList.add('is-active');
  }

  function completeStep(stepEl, summaryHtml) {
    if (!stepEl) return;
    stepEl.classList.remove('is-active', 'is-locked');
    stepEl.classList.add('is-done');
    var sum = stepEl.querySelector('.dcoo-step-summary');
    if (sum) { sum.hidden = false; sum.innerHTML = summaryHtml || ''; }
    var ch = stepEl.querySelector('.dcoo-step-change');
    if (ch) ch.hidden = false;
  }

  function reopenStep(stepEl) {
    if (!stepEl) return;
    stepEl.classList.remove('is-done', 'is-locked');
    stepEl.classList.add('is-active');
    var sum = stepEl.querySelector('.dcoo-step-summary');
    if (sum) sum.hidden = true;
    var ch = stepEl.querySelector('.dcoo-step-change');
    if (ch) ch.hidden = true;

    /* Step 1 special-case: reopening = "I want to reconsider my size".
       Clear the selected card highlights so the customer is back to
       the fresh-visit state and must deliberately tap a card again. */
    if (stepEl.id === 'dcoo-step-1') {
      $$('.dcoo-size-card').forEach(function(c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });
    }
  }

  function reopenStepWithScroll(stepEl) {
    if (!stepEl) return;
    reopenStep(stepEl);
    var titleEl = stepEl.querySelector('.dcoo-step-title') || stepEl;
    var top = titleEl.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function bindStepChangeButtons() {
    $$('.dcoo-step-change').forEach(function(btn) {
      on(btn, 'click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var stepEl = btn.closest('.dcoo-step');
        reopenStepWithScroll(stepEl);
      });
    });

    /* Whole step head clickable when done — same pattern as themed builder */
    $$('.dcoo-step-head').forEach(function(head) {
      on(head, 'click', function(e) {
        var stepEl = head.closest('.dcoo-step');
        if (!stepEl) return;
        if (!stepEl.classList.contains('is-done')) return;
        if (e.target.closest('.dcoo-step-change')) return;
        reopenStepWithScroll(stepEl);
      });
    });
  }

  /* ============================================================
     STEP 1 — SIZE TOGGLE

     UX flow:
       First visit  → both size cards unselected, Step 1 active,
                      Step 2 locked. Customer must actively pick.
       Tap a card   → that card marks .is-selected, Step 1 completes,
                      Step 2 unlocks.
       Tap OTHER size → page navigates to that variant's URL with a
                        ?dcoo_chose=1 query param. On reload, that
                        param triggers Step 1 auto-completion for the
                        new variant (so the customer doesn't have to
                        tap twice). The param is then stripped from
                        the URL via history.replaceState so it doesn't
                        persist if they share/copy the link.

     Why URL params instead of sessionStorage:
     - sessionStorage flags persist across tab refreshes in confusing
       ways (e.g. you tested once, now every visit looks "already chose")
     - URL params are tied to a single navigation event — cleaner
     - Visible in the URL bar = easier to debug
     ============================================================ */
  var CHOOSE_PARAM = 'dcoo_chose';

  function markSizeCardSelected(size) {
    $$('.dcoo-size-card').forEach(function(c) {
      var match = parseInt(c.dataset.size, 10) === size;
      c.classList.toggle('is-selected', match);
      c.setAttribute('aria-pressed', match ? 'true' : 'false');
    });
  }

  function completeSizeStep(size, price) {
    markSizeCardSelected(size);
    completeStep($('dcoo-step-1'),
      '<span class="dcoo-step-summary-text">Box of ' + size + ' · ' + money(price) + '</span>');
    unlockStep($('dcoo-step-2'));
    updateSticky();
  }

  function bindSizeCards() {
    $$('.dcoo-size-card').forEach(function(card) {
      on(card, 'click', function(e) {
        e.preventDefault();
        var size      = parseInt(card.dataset.size, 10);
        var variantId = parseInt(card.dataset.variantId, 10);
        var price     = parseInt(card.dataset.variantPrice, 10);

        if (size !== state.boxSize) {
          /* Customer picked the OTHER size. Swap the variant in-place
             without reloading the page — cookie-multipack is one
             Shopify product with two variants, so we just need to
             update the variant id (used at cart-submit time) and
             rebuild the box slots grid. No need to navigate to the
             variant URL like the cupcake builders do (those are two
             separate products). */
          swapBoxSize(size, variantId, price);
        }

        completeSizeStep(size, price);
      });
    });
  }

  /* In-place box-size swap. Updates state, rebuilds the box slots
     in the DOM, updates the parent's CSS modifier class for the
     grid layout, clears any cookies that were already in the box
     (since switching sizes invalidates the partial fill), and
     re-binds click handlers on the new slot buttons. */
  function swapBoxSize(newSize, newVariantId, newPrice) {
    state.boxSize        = newSize;
    state.currentVariant = newVariantId;
    state.currentPrice   = newPrice;
    state.cookies        = [];  /* size change invalidates partial fill */

    /* Update the modifier class on the box wrapper so CSS picks up
       the new grid template (6-cell vs 12-cell layouts may differ). */
    var box = $('dcoo-box');
    if (box) {
      box.classList.remove('dcoo-box--size-6', 'dcoo-box--size-12');
      box.classList.add('dcoo-box--size-' + newSize);
    }

    /* Rebuild slot buttons inside the grid. We replace innerHTML
       entirely — cheaper than diffing, and we re-bind handlers in
       one pass right after. */
    var grid = $('dcoo-box-grid');
    if (grid) {
      var slotsHtml = '';
      for (var i = 1; i <= newSize; i++) {
        slotsHtml += '<button type="button" class="dcoo-box-slot" ' +
                       'id="dcoo-box-slot-' + i + '" ' +
                       'data-slot="' + i + '" ' +
                       'aria-label="Cookie slot ' + i + ', empty">' +
                       '<span class="dcoo-box-slot-empty" aria-hidden="true">+</span>' +
                     '</button>';
      }
      grid.innerHTML = slotsHtml;
      /* Re-bind click handlers on the freshly-created buttons. */
      bindBoxSlots();
    }

    /* Re-render everything that depends on box size: empty slots,
       sticky bar headline ("N more cookies to add"), breakdown list. */
    renderBox();
    renderFlavorCounts();
    renderBreakdown();
  }

  /* On page load, decide whether Step 1 is open (first visit) or
     auto-completes because the customer just navigated here from
     the size toggle on the other variant. */
  function initStep1State() {
    var params = new URLSearchParams(window.location.search);
    var alreadyChose = params.get(CHOOSE_PARAM) === '1';
    if (alreadyChose) {
      completeSizeStep(state.boxSize, state.currentPrice);
      /* Strip the param so reloading or bookmarking the page
         doesn't preserve this auto-complete behavior. */
      params.delete(CHOOSE_PARAM);
      var newSearch = params.toString();
      var newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
      try { window.history.replaceState({}, '', newUrl); } catch (e) {}
    }
    /* else: Step 1 stays .is-active (its initial class from Liquid),
       Step 2 stays .is-locked. Customer must tap a card to proceed. */
  }

  /* ============================================================
     STEP 2 — FILL YOUR BOX
     Free-pick model: customer taps any flavor card, that flavor is
     pushed to state.cookies. The next empty box slot fills with
     that cookie. Cap at boxSize. Tapping a filled box slot removes
     that specific cookie (state.cookies array gets spliced).

     We never need to "swap" cookies between slots — duplicates are
     allowed (customer can have 12 chocolate chips). Tap order is
     preserved so removal is intuitive (you remove the cookie you
     last added by tapping that slot).
     ============================================================ */
  /* Read a card's live stock quantity. 9999 sentinel (or a missing/
     unparseable attribute) means untracked → Infinity, no cap. */
  function flavorQty(card) {
    var q = parseInt(card.dataset.flavorQty, 10);
    if (isNaN(q) || q >= 9999) return Infinity;
    return q;
  }

  /* Count how many of a given flavor are currently in the box. */
  function countOfFlavor(name) {
    var n = 0;
    state.cookies.forEach(function(c) { if (c.name === name) n++; });
    return n;
  }

  /* Capped-tap feedback: shake the card and flash a transient
     "Only N available" message over the image. Guarded so rapid
     taps don't stack multiple messages. */
  function flashMaxed(card, qty) {
    card.classList.add('is-shake');
    setTimeout(function() { card.classList.remove('is-shake'); }, 450);
    var wrap = card.querySelector('.dcoo-flavor-img-wrap');
    if (!wrap || wrap.querySelector('.dcoo-flavor-maxmsg')) return;
    var msg = document.createElement('span');
    msg.className = 'dcoo-flavor-maxmsg';
    msg.textContent = 'Only ' + qty + ' available';
    wrap.appendChild(msg);
    setTimeout(function() {
      if (msg.parentNode) msg.parentNode.removeChild(msg);
    }, 1800);
  }

  function bindFlavorCards() {
    $$('.dcoo-flavor-card').forEach(function(card) {
      on(card, 'click', function(e) {
        e.preventDefault();
        /* SOLD-OUT BACKSTOP (2026-06-12): Liquid renders sold-out
           cards with `disabled` + data-sold-out="true", which already
           blocks clicks at the browser level. This guard is belt-and-
           suspenders in case the disabled attribute is ever lost to
           markup/CSS drift — a sold-out cookie must never enter the box. */
        if (card.disabled || card.dataset.soldOut === 'true') return;
        if (state.cookies.length >= state.boxSize) {
          /* Box is full — don't add. Could shake the sticky bar or
             flash a message; for now we just no-op. The sticky bar
             already says "Box full" so the signal is clear. */
          return;
        }
        var name = card.dataset.flavorName;
        var key  = card.dataset.flavorKey;
        var tier = parseInt(card.dataset.flavorTier, 10) || 0;
        /* Shopify product handle (2026-06-12c) — carried through to
           the _cookie_inv line item property so the inventory
           decrement script works off handles, not display names. */
        var handle = card.dataset.flavorHandle || '';

        /* QUANTITY CAP (2026-06-12b): can't put more of a flavor in
           the box than Shopify says we have. The "+" simply doesn't
           move; the card shakes and tells them how many exist. */
        var qty = flavorQty(card);
        if (countOfFlavor(name) >= qty) {
          flashMaxed(card, qty);
          return;
        }

        state.cookies.push({ name: name, key: key, tier: tier, handle: handle });
        renderBox();
        renderFlavorCounts();
        renderBreakdown();
        updateStep2State();
        updateSticky();
      });
    });
  }

  function bindBoxSlots() {
    /* Tap a filled slot to remove that cookie. Empty slots no-op. */
    $$('.dcoo-box-slot').forEach(function(slot) {
      on(slot, 'click', function(e) {
        e.preventDefault();
        var slotIdx = parseInt(slot.dataset.slot, 10) - 1;
        if (slotIdx < 0 || slotIdx >= state.cookies.length) return;
        state.cookies.splice(slotIdx, 1);
        renderBox();
        renderFlavorCounts();
        renderBreakdown();
        updateStep2State();
        updateSticky();
      });
    });
  }

  /* Paint the box slots from state.cookies. Slots after the last
     filled show the empty "+" affordance. */
  function renderBox() {
    /* Find the source flavor card for each cookie so we can grab its
       image URL and reuse it inside the box slot. Lookups are by
       data-flavor-name match. We cache the lookup once per call.
       Markup is <div class="dcoo-flavor-img has-image"><img></div>
       so we query the inner <img> directly. Placeholder cards have
       no nested img — those fall back to the emoji glyph below. */
    var imgByName = {};
    $$('.dcoo-flavor-card').forEach(function(card) {
      var n = card.dataset.flavorName;
      var img = card.querySelector('.dcoo-flavor-img.has-image img');
      if (img) {
        imgByName[n] = img.getAttribute('src');
      } else {
        imgByName[n] = null;  /* placeholder emoji */
      }
    });

    for (var i = 1; i <= state.boxSize; i++) {
      var slot = $('dcoo-box-slot-' + i);
      if (!slot) continue;
      var cookie = state.cookies[i - 1];
      /* Clear previous contents (everything except .dcoo-box-slot-empty). */
      var emptySpan = slot.querySelector('.dcoo-box-slot-empty');
      slot.innerHTML = '';
      if (emptySpan) slot.appendChild(emptySpan);

      if (cookie) {
        slot.classList.add('is-filled');
        slot.setAttribute('aria-label', 'Cookie slot ' + i + ': ' + cookie.name + ' — tap to remove');
        var imgUrl = imgByName[cookie.name];
        if (imgUrl) {
          var img = document.createElement('img');
          img.src = imgUrl;
          img.alt = '';
          img.className = 'dcoo-box-slot-img';
          slot.appendChild(img);
        } else {
          var span = document.createElement('span');
          span.className = 'dcoo-box-slot-emoji';
          span.textContent = '🍪';
          slot.appendChild(span);
        }
      } else {
        slot.classList.remove('is-filled');
        slot.setAttribute('aria-label', 'Cookie slot ' + i + ', empty');
        /* Re-add the empty "+" span since innerHTML cleared it */
        if (!emptySpan) {
          var plus = document.createElement('span');
          plus.className = 'dcoo-box-slot-empty';
          plus.setAttribute('aria-hidden', 'true');
          plus.textContent = '+';
          slot.appendChild(plus);
        }
      }
    }

    /* Counter pill in step head */
    setText('dcoo-step2-counter', state.cookies.length + ' / ' + state.boxSize);

    /* Hint text under the box */
    var hint = $('dcoo-box-hint');
    if (hint) {
      if (state.cookies.length === 0) {
        hint.textContent = 'Tap a flavor on the right to add';
      } else if (state.cookies.length < state.boxSize) {
        hint.textContent = 'Tap a cookie above to remove it';
      } else {
        hint.textContent = 'Box full · Tap a cookie above to remove and swap';
      }
    }
  }

  /* Update count badges + is-selected state on each flavor card. Counts
     derive from state.cookies (sum of entries with matching name).
     The is-selected class drives a soft beige backdrop disc behind the
     image so customers can see at a glance which flavors are in the box. */
  function renderFlavorCounts() {
    var counts = {};
    state.cookies.forEach(function(c) {
      counts[c.name] = (counts[c.name] || 0) + 1;
    });
    $$('.dcoo-flavor-card').forEach(function(card) {
      var n = card.dataset.flavorName;
      var key = card.dataset.flavorKey;
      var badge = $('dcoo-flavor-count-' + key);
      var c = counts[n] || 0;
      card.classList.toggle('is-selected', c > 0);

      /* QUANTITY CAP (2026-06-12b): mark the card maxed when the box
         holds everything in stock. Runs on every add/remove, so
         removing one from the box automatically un-maxes the card. */
      var qty = flavorQty(card);
      card.classList.toggle('is-maxed', isFinite(qty) && c >= qty);

      if (!badge) return;
      if (c === 0) {
        badge.hidden = true;
        badge.textContent = '0';
      } else {
        badge.hidden = false;
        badge.textContent = String(c);
      }
    });
  }

  /* Render the "In your box" breakdown — name × count + upcharge. */
  function renderBreakdown() {
    var listEl = $('dcoo-breakdown-list');
    var wrapper = $('dcoo-breakdown');
    if (!listEl || !wrapper) return;

    if (state.cookies.length === 0) {
      wrapper.hidden = true;
      listEl.innerHTML = '';
      return;
    }

    /* Aggregate counts and tiers. Tier is the same for any given name
       since cookies are uniquely identified by name + their tier in
       the master list. */
    var agg = {};  /* { name: { count, tier } } */
    state.cookies.forEach(function(c) {
      if (!agg[c.name]) agg[c.name] = { count: 0, tier: c.tier };
      agg[c.name].count++;
    });

    /* Sort by name for stable display, but premium cookies could
       reasonably go to the bottom. Keeping alphabetical for now to
       match how the breakdown reads in the mockup. */
    var names = Object.keys(agg).sort();
    var html = names.map(function(name) {
      var row = agg[name];
      var lineUpcharge;
      if (row.tier === 1) {
        lineUpcharge = '<span class="dcoo-breakdown-item-upcharge">+$' + row.count + '</span>';
      } else if (row.tier === 2) {
        lineUpcharge = '<span class="dcoo-breakdown-item-upcharge">+$' + (row.count * 2) + '</span>';
      } else {
        lineUpcharge = '<span class="dcoo-breakdown-item-incl">incl.</span>';
      }
      return '<div class="dcoo-breakdown-item"><span>' + escapeHtml(name) + ' × ' + row.count + '</span>' + lineUpcharge + '</div>';
    }).join('');

    listEl.innerHTML = html;
    wrapper.hidden = false;
  }

  /* When the box becomes full, the step is "ready" — but we don't
     auto-complete it (customer might want to keep swapping). Step 3
     unlocks instead so they can add a gift message + candles in
     parallel with any last-minute cookie swaps. */
  function updateStep2State() {
    if (state.cookies.length === state.boxSize) {
      /* Box full — make sure step 3 is unlocked but DON'T collapse step 2. */
      var s3 = $('dcoo-step-3');
      if (s3 && s3.classList.contains('is-locked')) {
        unlockStep(s3);
      }
    }
  }

  /* ============================================================
     STEP 3 — MAKE IT SPECIAL
     Mirrors cupcake builder's bindUpgrades exactly:
       - Addon-card row click → toggle .is-active + show/hide sub-panel
       - Type-pill click → toggle individual pill .is-active + show/hide
         its matching .dcoo-candle-sub section
       - Img-tile click → radio-select within data-tile-group
       - Number input → triggers updateSticky() to recompute candle total

     Selected addons are read from the DOM at submit time rather than
     tracked in a state object. Simpler and matches cupcake builder.
     ============================================================ */
  function bindGiftMsg() {
    /* Gift message textarea — read at submit time. No live tracking
       needed; the textarea's .value is the source of truth. */
    var ta = $('dcoo-gift-text');
    if (!ta) return;
    on(ta, 'input', function() {
      state.giftMsg = ta.value;
    });
  }

  function bindAddonCards() {
    /* Each .dcoo-addon-row toggles its parent card's .is-active state
       and shows/hides its sub-panel. aria-controls links the row to
       the panel by id (matches cupcake's accessibility pattern). */
    $$('.dcoo-addon-row').forEach(function(row) {
      var handleToggle = function(e) {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        var card = row.closest('.dcoo-addon-card');
        if (!card) return;
        var nowActive = !card.classList.contains('is-active');
        card.classList.toggle('is-active', nowActive);
        row.setAttribute('aria-expanded', String(nowActive));
        var panelId = row.getAttribute('aria-controls');
        if (panelId) {
          var panel = document.getElementById(panelId);
          if (panel) panel.hidden = !nowActive;
        }
        updateSticky();
      };
      on(row, 'click', handleToggle);
      on(row, 'keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') { handleToggle(e); }
      });
    });

    /* Type pills — Rainbow / Metallic / Number / Sparklers.
       Each is an independent toggle (not a radio). Toggling on
       reveals the matching .dcoo-candle-sub section. */
    $$('.dcoo-type-pill').forEach(function(pill) {
      on(pill, 'click', function() {
        var on = pill.getAttribute('aria-checked') !== 'true';
        pill.setAttribute('aria-checked', String(on));
        pill.classList.toggle('is-active', on);
        var type = pill.dataset.type;
        var sub = $('dcoo-candle-sub-' + type);
        if (sub) sub.hidden = !on;
        updateSticky();
      });
    });

    /* Img-tiles (color picker for metallic + number candles).
       Radio-select within a data-tile-group: clicking one deselects
       all others in the same group. */
    $$('.dcoo-img-tile').forEach(function(tile) {
      var handleSelect = function() {
        var group = tile.dataset.tileGroup;
        if (!group) return;
        $$('.dcoo-img-tile[data-tile-group="' + group + '"]').forEach(function(t) {
          t.classList.remove('is-selected');
          t.setAttribute('aria-checked', 'false');
        });
        tile.classList.add('is-selected');
        tile.setAttribute('aria-checked', 'true');
        updateSticky();
      };
      on(tile, 'click', handleSelect);
      on(tile, 'keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      });
    });

    /* Number candle input — every digit is +1 candle, so the price
       must recompute on each keystroke. */
    var numInput = $('dcoo-candle-number');
    if (numInput) {
      on(numInput, 'input', function() {
        updateSticky();
      });
    }
  }

  /* ============================================================
     ADDON HELPERS — DOM-driven, no state.addons tracking
     Mirrors cupcake builder pattern. The DOM is the source of
     truth for which addons are selected. These helpers crawl the
     DOM and return the aggregate price (computeAddonCents) or a
     list of cart items (collectAddonItems).
     ============================================================ */
  function computeAddonCents() {
    var total = 0;
    /* Only count addons inside cards that are .is-active. If the
       customer hasn't expanded the candles card, ignore everything
       inside it (a closed card means no selection regardless of
       what's clicked inside). */
    $$('.dcoo-addon-card.is-active').forEach(function(card) {
      $$('.dcoo-type-pill.is-active', card).forEach(function(pill) {
        var type = pill.dataset.type;
        if (type === 'rainbow' && cfg.addonRainbowPrice) {
          total += cfg.addonRainbowPrice;
        } else if (type === 'sparklers' && cfg.addonSparklersPrice) {
          total += cfg.addonSparklersPrice;
        } else if (type === 'metallic') {
          /* One color tile selected = its variant price */
          var sel = card.querySelector('.dcoo-img-tile[data-tile-group="candle-color-metallic"].is-selected');
          if (sel) total += parseInt(sel.dataset.tilePrice, 10) || 0;
        } else if (type === 'number') {
          var sel = card.querySelector('.dcoo-img-tile[data-tile-group="candle-color-number"].is-selected');
          var num = $('dcoo-candle-number');
          var digits = (num && num.value) ? String(num.value).length : 0;
          if (sel && digits > 0) {
            total += (parseInt(sel.dataset.tilePrice, 10) || 0) * digits;
          }
        }
      });
    });
    return total;
  }

  /* Returns an array of { id, label, price, quantity } items for cart
     submit. Used by submitToCart. */
  function collectAddonItems() {
    var items = [];
    $$('.dcoo-addon-card.is-active').forEach(function(card) {
      $$('.dcoo-type-pill.is-active', card).forEach(function(pill) {
        var type = pill.dataset.type;
        if (type === 'rainbow' && cfg.addonRainbowVariantId) {
          items.push({
            id: cfg.addonRainbowVariantId,
            label: 'Rainbow candles',
            price: cfg.addonRainbowPrice,
            quantity: 1
          });
        } else if (type === 'sparklers' && cfg.addonSparklersVariantId) {
          items.push({
            id: cfg.addonSparklersVariantId,
            label: 'Sparklers',
            price: cfg.addonSparklersPrice,
            quantity: 1
          });
        } else if (type === 'metallic') {
          var sel = card.querySelector('.dcoo-img-tile[data-tile-group="candle-color-metallic"].is-selected');
          if (sel) {
            items.push({
              id: parseInt(sel.dataset.tileVariantId, 10),
              label: 'Metallic candle (' + sel.dataset.tileValue + ')',
              price: parseInt(sel.dataset.tilePrice, 10) || 0,
              quantity: 1
            });
          }
        } else if (type === 'number') {
          var selN = card.querySelector('.dcoo-img-tile[data-tile-group="candle-color-number"].is-selected');
          var num = $('dcoo-candle-number');
          var digits = (num && num.value) ? String(num.value).length : 0;
          if (selN && digits > 0) {
            items.push({
              id: parseInt(selN.dataset.tileVariantId, 10),
              label: 'Number candle "' + num.value + '" (' + selN.dataset.tileValue + ')',
              price: parseInt(selN.dataset.tilePrice, 10) || 0,
              quantity: digits
            });
          }
        }
      });
    });
    return items;
  }

  /* ============================================================
     STICKY BAR — live total + ATC enable/disable
     ============================================================ */
  function updateSticky() {
    var headline = $('dcoo-sticky-headline');
    var totalEl  = $('dcoo-sticky-total');
    var atc      = $('dcoo-atc');
    if (!headline || !totalEl || !atc) return;

    var remaining = state.boxSize - state.cookies.length;

    /* Premium upcharge total (in cents) */
    var premiumCents = 0;
    state.cookies.forEach(function(c) {
      if (c.tier === 1) premiumCents += cfg.premiumTier1Price;
      else if (c.tier === 2) premiumCents += cfg.premiumTier2Price;
    });

    /* Make-it-special addon total — computed from the DOM since
       that's where selections live (cupcake builder pattern). Each
       active type pill adds its variant's price; number candles
       multiply price × digits in the input. */
    var addonCents = computeAddonCents();

    var totalCents = state.currentPrice + premiumCents + addonCents;

    if (remaining > 0) {
      headline.textContent = remaining + ' more cookie' + (remaining === 1 ? '' : 's') + ' to add';
      atc.disabled = true;
    } else {
      headline.textContent = 'Ready to order';
      atc.disabled = false;
    }

    var totalStr = 'Box of ' + state.boxSize + ' · ' + money(totalCents);
    if (premiumCents > 0) {
      totalStr += ' <span class="dcoo-sticky-total-premium">(' + money(state.currentPrice) + ' + ' + money(premiumCents) + ' premium';
      if (addonCents > 0) totalStr += ' + ' + money(addonCents) + ' add-ons';
      totalStr += ')</span>';
    } else if (addonCents > 0) {
      totalStr += ' <span class="dcoo-sticky-total-premium">(' + money(state.currentPrice) + ' + ' + money(addonCents) + ' add-ons)</span>';
    }
    totalEl.innerHTML = totalStr;
  }

  /* ============================================================
     ADD TO CART
     Two-stage:
       1) Add the cookie box variant alone (1 unit), grab line.key
       2) Batch all premium upcharges + addons into one /cart/add.js
          POST, linking each to the parent box via _addon_for.

     Line item properties on the box itself:
       - Cookie breakdown (human-readable summary of what's in the box)
       - Cookie list (machine-readable per-line)
       - Gift message (if filled)
     ============================================================ */
  function buildBoxLineProperties() {
    var props = {};

    /* Human-readable breakdown */
    var agg = {};
    state.cookies.forEach(function(c) {
      if (!agg[c.name]) agg[c.name] = 0;
      agg[c.name]++;
    });
    var summaryParts = Object.keys(agg).sort().map(function(n) {
      return n + ' × ' + agg[n];
    });
    props['Cookies'] = summaryParts.join(', ');

    /* Per-cookie list (for bakery production tickets) */
    props['Cookie order'] = state.cookies.map(function(c, i) {
      return '#' + (i + 1) + ': ' + c.name;
    }).join(' · ');

    /* Machine-readable inventory payload (2026-06-12c) — consumed by
       CookieInventory.gs in the autolist Apps Script project, which
       decrements each flavor's Shopify inventory on the order
       webhook. Format: "handle:count|handle:count", sorted. The
       underscore prefix hides it from cart/checkout display (same
       convention as _addon_for). Handles flow from COOKIE_MASTER via
       data-flavor-handle, so the Liquid stays the single source of
       truth for the flavor → product link. Flavors with a blank
       handle are skipped (nothing to decrement). */
    var invAgg = {};
    state.cookies.forEach(function(c) {
      if (!c.handle) return;
      invAgg[c.handle] = (invAgg[c.handle] || 0) + 1;
    });
    var invParts = Object.keys(invAgg).sort().map(function(h) {
      return h + ':' + invAgg[h];
    });
    if (invParts.length > 0) {
      props['_cookie_inv'] = invParts.join('|');
    }

    /* Gift message — only if filled AND the gift card is currently
       active. If the customer collapsed the card, treat the textarea
       value as discarded (closed card = no selection, same as candles). */
    var giftCard = $('dcoo-addon-card-gift');
    var giftActive = giftCard && giftCard.classList.contains('is-active');
    if (giftActive && state.giftMsg && state.giftMsg.trim() !== '') {
      props['Gift message'] = state.giftMsg.trim();
    }

    return props;
  }

  /* Build the items array for the second /cart/add.js call.
     Each premium cookie becomes one quantity of the appropriate
     tier upcharge product. Each chosen addon becomes one quantity
     of that addon. All carry _addon_for: <parent_key> so the cart
     template can group + roll up totals. */
  function buildAddonItems(parentKey) {
    var items = [];

    /* Premium upcharges — count by tier */
    var tier1Count = 0;
    var tier2Count = 0;
    state.cookies.forEach(function(c) {
      if (c.tier === 1) tier1Count++;
      else if (c.tier === 2) tier2Count++;
    });

    if (tier1Count > 0 && cfg.premiumTier1VariantId) {
      items.push({
        id: cfg.premiumTier1VariantId,
        quantity: tier1Count,
        properties: {
          '_addon_for': parentKey,
          '_addon': cfg.premiumTier1Label
        }
      });
    }
    if (tier2Count > 0 && cfg.premiumTier2VariantId) {
      items.push({
        id: cfg.premiumTier2VariantId,
        quantity: tier2Count,
        properties: {
          '_addon_for': parentKey,
          '_addon': cfg.premiumTier2Label
        }
      });
    }

    /* Make-it-special addons — read from DOM via collectAddonItems. */
    collectAddonItems().forEach(function(a) {
      items.push({
        id: a.id,
        quantity: a.quantity || 1,
        properties: {
          '_addon_for': parentKey,
          '_addon': a.label
        }
      });
    });

    return items;
  }

  function bindAtc() {
    var atc = $('dcoo-atc');
    if (!atc) return;
    on(atc, 'click', function(e) {
      e.preventDefault();
      if (atc.disabled) return;
      submitToCart();
    });
  }

  function submitToCart() {
    var atc = $('dcoo-atc');
    if (atc) {
      atc.classList.add('is-loading');
      atc.disabled = true;
    }

    /* Stage 1: add the box. */
    var boxPayload = {
      id: state.currentVariant,
      quantity: 1,
      properties: buildBoxLineProperties()
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(boxPayload)
    })
    .then(function(r) {
      if (!r.ok) throw new Error('Box add failed (HTTP ' + r.status + ')');
      return r.json();
    })
    .then(function(boxLine) {
      var parentKey = boxLine.key;
      var addonItems = buildAddonItems(parentKey);
      if (addonItems.length === 0) {
        /* Nothing extra — go straight to cart. */
        window.location.href = '/cart';
        return;
      }
      /* Stage 2: batch the addons. */
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: addonItems })
      }).then(function(r2) {
        if (!r2.ok) throw new Error('Addon batch failed (HTTP ' + r2.status + ')');
        return r2.json();
      }).then(function() {
        window.location.href = '/cart';
      });
    })
    .catch(function(err) {
      console.error('[dcoo] ATC error:', err);
      alert('Sorry — there was a problem adding your box to the cart. Please try again, or contact us if it keeps happening.');
      if (atc) {
        atc.classList.remove('is-loading');
        atc.disabled = false;
      }
    });
  }

  /* ============================================================
     INIT
     ============================================================ */
  function init() {
    bindStepChangeButtons();
    bindSizeCards();
    bindFlavorCards();
    bindBoxSlots();
    bindGiftMsg();
    bindAddonCards();
    bindAtc();

    renderBox();
    renderFlavorCounts();
    renderBreakdown();
    initStep1State();
    updateSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();